import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { request } from '@/lib/http';
import { ApiError } from '@/features/auth/api';
import { BADGE_COUNTS_QUERY_KEY } from '@/features/invoices/badges/queryKeys';
import { useRefreshList } from '@/features/invoices/useRefreshList';
import type { MarginProcedure } from '@/features/invoices/types';

/**
 * Invoice-draft API client. Mirrors the Ktor `/api/v1/invoice-drafts` contract 1:1 (see
 * `apps/api/.../routes/invoicing/model/`). The whole router is dark unless `me.issuingEnabled`
 * is true — when issuing is off every path 404s, so callers must gate on that flag.
 *
 * Money is exact decimal strings everywhere in this contract (grosz-exact `BigDecimal.toPlainString`),
 * never `Double` — treat every amount as a `string`.
 */

// --- Enums (server enum names on the wire) -------------------------------------------------------

/** Draft lifecycle. Only `ACTIVE` is editable/listed by default. */
export type DraftStatus = 'ACTIVE' | 'ISSUING' | 'CONSUMED';

/**
 * How VAT is accounted for — orthogonal to `DocumentKind`. A margin invoice is still a `STANDARD` kind
 * (`RodzajFaktury=VAT`); the scheme selects the amount structure, not the document type.
 */
export type TaxScheme = 'REGULAR' | 'MARGIN';

/**
 * The margin procedures of art. 119 / art. 120, shared with the synced invoice views. FA(3) requires
 * exactly one alongside the `PMarzy` marker, so a margin draft has no valid "unset" state once it is
 * being issued.
 */
export type { MarginProcedure };

/** The margin procedures the composer offers, in picker order. */
export const MARGIN_PROCEDURES: readonly MarginProcedure[] = [
  'USED_GOODS',
  'WORKS_OF_ART',
  'COLLECTIBLES_AND_ANTIQUES',
  'TRAVEL_AGENCY',
] as const;

/** All FA(3) document kinds; only `STANDARD` is constructible in v1 (POST always creates STANDARD). */
export type DocumentKind =
  | 'STANDARD'
  | 'CORRECTION'
  | 'ADVANCE'
  | 'SETTLEMENT'
  | 'SIMPLIFIED'
  | 'CORRECTION_OF_ADVANCE'
  | 'CORRECTION_OF_SETTLEMENT';

/** Full VatRate set the evaluator accepts. The composer only offers `V1_SELECTABLE_VAT_RATES`. */
export type VatRate =
  | 'STANDARD_23'
  | 'RATE_22'
  | 'REDUCED_8'
  | 'RATE_7'
  | 'REDUCED_5'
  | 'RATE_4'
  | 'RATE_3'
  | 'ZERO_DOMESTIC'
  | 'ZERO_WDT'
  | 'ZERO_EXPORT'
  | 'EXEMPT'
  | 'REVERSE_CHARGE'
  | 'NOT_SUBJECT_I'
  | 'NOT_SUBJECT_II';

/** The rates the v1 composer surfaces in the per-line picker (mirrors `VatRate.V1_SELECTABLE`). */
export const V1_SELECTABLE_VAT_RATES: readonly VatRate[] = [
  'STANDARD_23',
  'REDUCED_8',
  'REDUCED_5',
  'ZERO_DOMESTIC',
  'EXEMPT',
] as const;

export type PaymentMethod =
  | 'CASH'
  | 'CARD'
  | 'VOUCHER'
  | 'CHEQUE'
  | 'CREDIT'
  | 'TRANSFER'
  | 'MOBILE';

/** The payment methods the v1 composer surfaces (the evaluator accepts the full `PaymentMethod` set). */
export const V1_SELECTABLE_PAYMENT_METHODS: readonly PaymentMethod[] = [
  'TRANSFER',
  'CASH',
  'CARD',
  'MOBILE',
] as const;

// --- Draft content (the lenient composer body; shared by request + response) ---------------------

/**
 * One draft line, mirroring the API's `DraftLineDto`. Every field is raw/lenient — `quantity` and
 * `unitNetPrice` are decimal *strings* (not numbers), `vatRate` is a `VatRate` enum name. Gaps come
 * back as `fieldErrors`, never a 400.
 */
export interface DraftLineContent {
  name: string | null;
  unitOfMeasure: string | null;
  quantity: string | null;
  unitNetPrice: string | null;
  /** Gross unit price — the margin scheme's price field; only one of the two is ever filled. */
  unitGrossPrice: string | null;
  vatRate: string | null;
}

/** Payment block, mirroring the API's `DraftPaymentDto`. Always sent as an object (never null). */
export interface DraftPaymentContent {
  method: string | null;
  dueDate: string | null;
  /** Settlement date (`DataZaplaty`); when set the invoice is issued already-paid (`Zaplacono`). */
  paidDate: string | null;
  bankAccount: string | null;
}

/** Sale date, mirroring `DraftSaleDateDto`: a single `onDate`, XOR a `periodFrom`/`periodTo` span. */
export interface DraftSaleDateContent {
  onDate: string | null;
  periodFrom: string | null;
  periodTo: string | null;
}

/**
 * One additional-data (`DodatkowyOpis`) row, mirroring `DraftAdditionalDataDto`. Both parts are raw and
 * lenient; the evaluator normalizes and drops fully-blank rows, and flags a lone key/value or an
 * over-long entry.
 */
export interface DraftAdditionalDataContent {
  key: string | null;
  value: string | null;
}

/** The versioned draft body, mirroring `DraftContentDto`. `currencyCode` null defaults to PLN. */
export interface DraftContent {
  currencyCode: string | null;
  numberOverride: string | null;
  saleDate: DraftSaleDateContent | null;
  lines: DraftLineContent[];
  payment: DraftPaymentContent;
  exemptionLegalBasis: string | null;
  additionalData: DraftAdditionalDataContent[];
  footerNote: string | null;
  /** A `MarginProcedure` name — required on a margin draft, ignored on a regular one. */
  marginProcedure: string | null;
}

/** The `DraftContent` schema version the client speaks (mirrors `DraftContent.CURRENT_VERSION`). */
export const CONTENT_VERSION = 1;

/** A fresh, empty draft body — a single blank line, empty payment, no sale date. */
export function emptyDraftContent(): DraftContent {
  return {
    currencyCode: null,
    numberOverride: null,
    saleDate: null,
    lines: [emptyDraftLine()],
    payment: { method: null, dueDate: null, paidDate: null, bankAccount: null },
    exemptionLegalBasis: null,
    additionalData: [],
    footerNote: null,
    marginProcedure: null,
  };
}

export function emptyDraftLine(): DraftLineContent {
  return {
    name: null,
    unitOfMeasure: null,
    quantity: null,
    unitNetPrice: null,
    unitGrossPrice: null,
    vatRate: null,
  };
}

// --- Responses -----------------------------------------------------------------------------------

/** A full draft row, mirroring `DraftResponse`. `version` is the optimistic-lock counter. */
export interface DraftResponse {
  id: number;
  documentKind: DocumentKind;
  taxScheme: TaxScheme;
  customerId: number | null;
  content: DraftContent;
  contentVersion: number;
  buyerLabel: string | null;
  buyerNip: string | null;
  netTotal: string | null;
  vatTotal: string | null;
  grossTotal: string | null;
  status: DraftStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** A list-row projection (no `content` body), mirroring `DraftSummaryResponse`. */
export interface DraftSummary {
  id: number;
  documentKind: DocumentKind;
  taxScheme: TaxScheme;
  customerId: number | null;
  buyerLabel: string | null;
  buyerNip: string | null;
  netTotal: string | null;
  vatTotal: string | null;
  grossTotal: string | null;
  status: DraftStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** A page of draft summaries, mirroring `PagedDrafts`. `nextOffset` is null on the last page. */
export interface PagedDrafts {
  items: DraftSummary[];
  nextOffset: number | null;
}

/** Machine-readable structural gaps the evaluator emits; the client owns the copy (see i18n). */
export type DraftFieldErrorCode =
  | 'PROFILE_NOT_READY'
  | 'NO_BUYER'
  | 'NO_LINES'
  | 'LINE_NAME_MISSING'
  | 'LINE_NAME_TOO_LONG'
  | 'LINE_QUANTITY_INVALID'
  | 'LINE_UNIT_PRICE_INVALID'
  | 'LINE_VAT_RATE_INVALID'
  | 'SALE_DATE_MISSING'
  | 'SALE_DATE_INVALID'
  | 'CURRENCY_INVALID'
  | 'PAYMENT_METHOD_INVALID'
  | 'PAYMENT_DUE_DATE_INVALID'
  | 'PAYMENT_PAID_DATE_INVALID'
  | 'PAYMENT_ACCOUNT_INVALID'
  | 'ADDITIONAL_DATA_KEY_MISSING'
  | 'ADDITIONAL_DATA_VALUE_MISSING'
  | 'ADDITIONAL_DATA_TOO_LONG'
  | 'FOOTER_NOTE_TOO_LONG'
  | 'LINE_UNIT_GROSS_PRICE_INVALID'
  | 'MARGIN_PROCEDURE_MISSING';

/**
 * A field error, mirroring `DraftFieldErrorResponse`. `line` is the 1-based row for the `LINE_*` codes
 * and the 1-based additional-data row for the `ADDITIONAL_DATA_*` codes.
 */
export interface DraftFieldError {
  code: DraftFieldErrorCode;
  line: number | null;
}

/** A computed line total (over lines that parsed), mirroring `ComputedLineResponse`. */
/**
 * A computed line total. A regular line reports `net`/`vat`/`rate`; a margin line discloses none of
 * those and reports `gross` instead, so every amount is optional on the wire.
 */
export interface ComputedLine {
  lineNumber: number;
  net?: string | null;
  vat?: string | null;
  gross?: string | null;
  rate?: VatRate | null;
}

/** A per-rate aggregate, mirroring `RateSummaryResponse`. */
export interface RateSummary {
  rate: VatRate;
  net: string;
  vat: string;
}

/** Live totals, mirroring `TotalsResponse`; null until at least one line fully parses. */
export interface ComputedTotals {
  currency: string;
  lines: ComputedLine[];
  perRate: RateSummary[];
  totalNet: string;
  totalVat: string;
  totalGross: string;
}

export type FindingSeverity = 'BLOCK' | 'WARN';

/**
 * A rule-engine finding, mirroring `FindingResponse`. Unlike field errors, findings carry their own
 * bilingual copy (`messageEn`/`messagePl`) — render the one matching the active language.
 */
export interface Finding {
  id: string;
  severity: FindingSeverity;
  legalBasis: string;
  messageEn: string;
  messagePl: string;
}

/** Blockers + warnings, mirroring `ValidationResponse`. Empty until the whole document assembles. */
export interface ValidationReport {
  blockers: Finding[];
  warnings: Finding[];
  isIssuable: boolean;
}

/** The full evaluation, mirroring `DraftEvaluationResponse`. */
export interface DraftEvaluation {
  fieldErrors: DraftFieldError[];
  computedTotals: ComputedTotals | null;
  validation: ValidationReport;
  suggestedNumber: string | null;
  isIssuable: boolean;
}

/**
 * The evaluation shown before a draft exists — a not-yet-created ("new") invoice has no server row to
 * evaluate, so the composer renders a clean form (no field errors, no suggested number) until the first
 * meaningful edit creates the row and the live `validate` loop takes over.
 */
export const EMPTY_EVALUATION: DraftEvaluation = {
  fieldErrors: [],
  computedTotals: null,
  validation: { blockers: [], warnings: [], isIssuable: false },
  suggestedNumber: null,
  isIssuable: false,
};

/** A draft plus its fresh evaluation, mirroring `DraftDetailResponse` (GET/{id} and PUT/{id}). */
export interface DraftDetail {
  draft: DraftResponse;
  evaluation: DraftEvaluation;
}

// --- Request bodies ------------------------------------------------------------------------------

/**
 * Autosave body, mirroring `DraftUpdateRequest`. Note the field is `expectedVersion` (the `version`
 * last read) — a mismatch yields 409 `VERSION_CONFLICT`.
 */
export interface DraftUpdateRequest {
  expectedVersion: number;
  customerId: number | null;
  content: DraftContent;
  contentVersion: number;
}

/** Live-validate body, mirroring `DraftValidateRequest`. No version lock — never persists, never 409s. */
export interface DraftValidateRequest {
  customerId: number | null;
  content: DraftContent;
  contentVersion: number;
}

// --- Hooks ---------------------------------------------------------------------------------------

export const INVOICE_DRAFTS_QUERY_KEY = ['invoice-drafts'] as const;

/** Page size for the drafts list — matches the API default. */
export const DRAFTS_PAGE_SIZE = 50;

async function fetchDraftsPage(offset: number): Promise<PagedDrafts> {
  const params = new URLSearchParams({
    status: 'ACTIVE',
    limit: String(DRAFTS_PAGE_SIZE),
    offset: String(offset),
  });
  return request<PagedDrafts>(`/api/v1/invoice-drafts?${params.toString()}`);
}

/** In-progress (ACTIVE) drafts, most-recently-updated first, fetched a page at a time. */
export function useDrafts() {
  return useInfiniteQuery({
    queryKey: [...INVOICE_DRAFTS_QUERY_KEY, 'list'],
    queryFn: ({ pageParam }) => fetchDraftsPage(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Manual refresh of the drafts list: collapse to page 1, refetch, and pull the sidebar badge counts
 * alongside so the nav pill agrees with what the user just pulled by hand.
 *
 * Scoped to the `'list'` sub-key, never the bare `INVOICE_DRAFTS_QUERY_KEY` prefix: `useDraftAutosave`
 * owns `[...INVOICE_DRAFTS_QUERY_KEY, 'detail', id]` through `setQueryData`, and invalidating that would
 * fight the composer's version lock. Scoping also keeps the button's spinner from reacting to composer
 * autosave traffic.
 */
export function useRefreshDrafts() {
  return useRefreshList([...INVOICE_DRAFTS_QUERY_KEY, 'list'], { also: [BADGE_COUNTS_QUERY_KEY] });
}

/**
 * Loads a single draft plus its evaluation (`GET /{id}`). The composer seeds local editor state from
 * this once, so refetch-on-focus is disabled to avoid clobbering in-flight edits.
 */
export function useDraft(id: number) {
  return useQuery<DraftDetail>({
    queryKey: [...INVOICE_DRAFTS_QUERY_KEY, 'detail', id],
    queryFn: () => request<DraftDetail>(`/api/v1/invoice-drafts/${id}`),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Creates an empty STANDARD draft (`POST`), optionally pre-linking a customer (`?customerId=`). A plain
 * function (not a hook) because lazy-create fires it from the composer's edit loop — the row is created
 * on the *first meaningful edit*, not on entering `/invoices/new/standard`, so there's never an abandoned blank
 * draft. Returns the fresh row (with its `version`); the caller adopts the id and drives the normal
 * by-id `validate` + `PUT` loop from there. Throws `ApiError` on failure.
 */
export function createDraft(
  customerId?: number | null,
  taxScheme: TaxScheme = 'REGULAR',
): Promise<DraftResponse> {
  const params = new URLSearchParams();
  if (customerId != null) params.set('customerId', String(customerId));
  if (taxScheme !== 'REGULAR') params.set('taxScheme', taxScheme);
  const query = params.size > 0 ? `?${params.toString()}` : '';
  return request<DraftResponse>(`/api/v1/invoice-drafts${query}`, { method: 'POST' });
}

/**
 * Version-locked autosave (`PUT /{id}`). Returns the saved draft (incremented `version`) + a fresh
 * evaluation. A plain function (not a hook) because the composer drives it from debounce timers and
 * needs it to keep working across unmount (navigate-away flush) — see `useDraftAutosave`. Throws
 * `ApiError` with `code === 'VERSION_CONFLICT'` (409) or `'DRAFT_NOT_EDITABLE'` (409).
 */
export function saveDraft(id: number, body: DraftUpdateRequest): Promise<DraftDetail> {
  return request<DraftDetail>(`/api/v1/invoice-drafts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Lock-free live validation (`POST /{id}/validate`) of the current editor content — never persists. */
export function validateDraftContent(
  id: number,
  body: DraftValidateRequest,
): Promise<DraftEvaluation> {
  return request<DraftEvaluation>(`/api/v1/invoice-drafts/${id}/validate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function useDeleteDraft() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => request<void>(`/api/v1/invoice-drafts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...INVOICE_DRAFTS_QUERY_KEY, 'list'] });
      void qc.invalidateQueries({ queryKey: BADGE_COUNTS_QUERY_KEY });
    },
  });
}

/**
 * Fetches the server-rendered FA(3) XML for a *saved* draft (`GET /{id}/preview-xml`). The server runs
 * the real `Fa3XmlWriter` + XSD validation, so this is the authoritative render. Throws `ApiError`
 * (with `code === 'DRAFT_NOT_RENDERABLE'` on 422) when the draft can't assemble. Flush autosave first —
 * this renders the persisted row, not the live editor content.
 */
export async function fetchDraftPreviewXml(id: number): Promise<Blob> {
  const res = await fetch(`/api/v1/invoice-drafts/${id}/preview-xml`, {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }
  return res.blob();
}
